'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "defaultValue", "dropdownClassName", "groupType", "hovered", "menuHovered", "onMenuHoveredChange", "options", "popoverProps", "value", "onChange"],
  _excluded2 = ["hovered"];
import classNames from 'classnames';
import { EERIE, GYPSUM } from 'HubStyleTokens/colors';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'styled-components';
import { callIfPossible } from '../../core/Functions';
import SyntheticEvent from '../../core/SyntheticEvent';
import Controllable from '../../decorators/Controllable';
import UIIcon from '../../icon/UIIcon';
import HoverProvider from '../../providers/HoverProvider';
import { moveFocusToNext } from '../../utils/Dom';
import UIAbstractDropdown from '../abstract/UIAbstractDropdown';
import { renderMenu } from '../utils/renderMultiLevelMenu';
import { jsx as _jsx } from "react/jsx-runtime";
const SubDropdown = styled(UIAbstractDropdown).withConfig({
  displayName: "SubDropdownMenu__SubDropdown",
  componentId: "sc-1bgijxg-0"
})(["&&&{background-color:", ";}"], ({
  open
}) => open ? GYPSUM : null);
SubDropdown.displayName = 'SubDropdownMenu__SubDropdown';
const RightCaret = () => /*#__PURE__*/_jsx(UIIcon, {
  name: "right",
  size: "xxs",
  color: EERIE
});
class SubDropdownMenu extends PureComponent {
  constructor(...args) {
    super(...args);
    this.handleKeyDown = evt => {
      const {
        key
      } = evt;
      const {
        onMenuHoveredChange
      } = this.props;
      if (key === 'ArrowRight' || key === 'Enter' || key === ' ') {
        // allow Enter to open menu without closing entire dropdown
        if (!this.getComputedOpen() && key === 'Enter') evt.preventDefault();
        onMenuHoveredChange(SyntheticEvent(true));
        evt.stopPropagation();
      }
    };
    this.handleKeyUp = evt => {
      const {
        key
      } = evt;
      if (key === 'ArrowRight' || key === 'Enter' || key === ' ') {
        // focus the first item in the submenu after opening
        const firstChild = this._menuEl.querySelector('li');
        if (firstChild) {
          firstChild.children[0].focus();
        }
        evt.preventDefault();
        evt.stopPropagation();
      }
    };
    this.handleKeyUpOnMenu = evt => {
      const {
        key
      } = evt;
      const {
        onMenuHoveredChange
      } = this.props;
      // stopPropagation to prevent UIAbstractDropdown from closing ~all menus~ on Escape
      if (key === 'Escape') {
        onMenuHoveredChange(SyntheticEvent(false));
        evt.stopPropagation();
        return;
      }
    };
    this.handleKeyDownOnMenu = evt => {
      const {
        key
      } = evt;
      const {
        onMenuHoveredChange
      } = this.props;
      if (!this.getComputedOpen()) return;
      if (key === 'ArrowLeft') {
        onMenuHoveredChange(SyntheticEvent(false));
        // prevents closing ~all~ submenus on ArrowLeft
        evt.stopPropagation();
        return;
      }

      // Up/Down arrow handling like in UIDropdown
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        if (moveFocusToNext('li', this._menuEl, key === 'ArrowUp')) {
          evt.preventDefault();
        }
        evt.stopPropagation();
      }
    };
    this.menuRefCallback = ref => {
      this._menuEl = ref;
    };
    this.renderChildren = ({
      options,
      groupType,
      value,
      onChange
    }) => {
      let updatedOptions = options;
      // don't do anything special if not handling the radio/"selectable" case
      if (groupType === 'radio') {
        updatedOptions = updatedOptions.map(item => {
          const option = Object.assign({}, item);
          option.selected = value === item.value;
          // copy any existing onClick behavior on option and merge in value change behavior
          option.onClick = evt => {
            // keep the menu open by ensuring the parent menu doesn't handle closing
            evt.preventDefault();
            evt.stopPropagation();
            // only trigger when value changes
            if (this.props.value !== option.value) {
              onChange(SyntheticEvent(option.value));
            }
            callIfPossible(item.onClick, evt);
          };
          return option;
        });
      }
      return renderMenu(updatedOptions);
    };
  }
  getComputedOpen() {
    const {
      menuHovered,
      open
    } = this.props;
    return open || this._hovered || menuHovered;
  }
  render() {
    const _this$props = this.props,
      {
        className,
        defaultValue,
        dropdownClassName,
        groupType,
        onMenuHoveredChange,
        options,
        popoverProps,
        value,
        onChange
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const computedDropdownClassName = classNames(className, dropdownClassName, 'private-dropdown--list');
    return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, this.props, {
      children: hoverProviderProps => {
        const {
            hovered
          } = hoverProviderProps,
          hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded2);
        this._hovered = hovered;
        const computedOpen = this.getComputedOpen();
        return /*#__PURE__*/_jsx(SubDropdown, Object.assign({}, rest, hoverProviderRestProps, {
          popoverProps: Object.assign({
            _distanceAdjustment: -4,
            _insetAdjustment: 8
          }, popoverProps, {
            onMouseEnter: () => onMenuHoveredChange(SyntheticEvent(true)),
            onMouseLeave: () => onMenuHoveredChange(SyntheticEvent(false)),
            onKeyDown: this.handleKeyDownOnMenu,
            onKeyUp: this.handleKeyUpOnMenu
          }),
          caretRenderer: RightCaret,
          dropdownClassName: computedDropdownClassName,
          placement: "right bottom",
          open: computedOpen,
          onKeyUp: this.handleKeyUp,
          onKeyDown: this.handleKeyDown
          // mount animation looks off - keep disabled for now
          ,
          animateOnToggle: false,
          autoPlacement: "horiz",
          children: /*#__PURE__*/_jsx("div", {
            "data-dropdown-menu": true,
            ref: this.menuRefCallback,
            children: this.renderChildren({
              options,
              groupType,
              value: value || defaultValue,
              onChange
            })
          })
        }));
      }
    }));
  }
}
SubDropdownMenu.displayName = 'SubDropdownMenu';
SubDropdownMenu.defaultProps = Object.assign({}, UIAbstractDropdown.defaultProps, {
  groupType: 'none',
  menuWidth: 'auto'
});
SubDropdownMenu.propTypes = Object.assign({}, UIAbstractDropdown.propTypes, {
  groupType: PropTypes.oneOf(['none', 'radio']),
  hovered: PropTypes.bool,
  open: PropTypes.bool,
  value: PropTypes.string
});
export default Controllable(SubDropdownMenu, ['menuHovered', 'value']);